import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaTimes, FaArrowLeft } from "react-icons/fa";

// కథాజాలం · Story Graph (Bhaskar, 2026-10-20): the whole story page as a
// node graph.
//
// Story view: the story is the star in the middle; its media (picture story,
// LIVE story / analysis, podcast, reels, శ్రీమతి వసుంధర విశ్లేషణ, the profile)
// sit on the upper ring; related-story groups (same story, storyteller,
// writer, genre, this episode) hang below as hubs on a radial outer ring.
//
// Focus view: tapping a hub (or its "+N") glides it into the centre — it
// becomes the star (the storyteller with their photo, the episode, the genre
// tags…) and every one of its stories opens in a circle around it. The story
// shrinks into the corner; tapping it, the breadcrumb or Esc flies back.
// Like moving around a map: nodes that exist in both views glide between
// them, the rest fade in / out.
//
// Any story node opens that story's own graph. Media nodes open in an
// overlay via renderMedia(node). Plain SVG, no graph library.
//
// Props:
//   center   { title, subtitle, photo, onClick }
//   media    [{ id, label, sub, image, Icon, color }]
//   groups   [{ id, label, sub, color, focusTitle, focusSub, focusImage,
//               items: [{ key, title, sub, to }] }]
//   renderMedia(node) → JSX for the overlay

const W = 1000;
const H = 860;
const STORY_C = [500, 330]; // centre in the story view
const FOCUS_C = [500, 430]; // centre in a focus view
const R3 = 330; // story view: outer ring of related stories
const MAX_LEAVES = 6; // per group in the story view (the rest behind "+N")

const deg = (a) => (a * Math.PI) / 180;
const polar = ([cx, cy], r, a) => [cx + r * Math.cos(deg(a)), cy + r * Math.sin(deg(a))];
const spread = (n, from, to) =>
  n <= 1 ? [(from + to) / 2] : Array.from({ length: n }, (_, i) => from + ((to - from) * i) / (n - 1));
const clip = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "");
const norm = (a) => ((((a + 180) % 360) + 360) % 360) - 180; // → [-180, 180)

// Quadratic edge bowed slightly sideways, so edges read as threads.
const edgePath = ([x1, y1], [x2, y2], bow = 0.12) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  return `M${x1},${y1} Q${mx - (y2 - y1) * bow},${my + (x2 - x1) * bow} ${x2},${y2}`;
};

// ── Story view: media on the upper half-ring; related stories on one outer
// ring across the lower half, each group in its own arc segment with its hub
// at the middle of that segment.
function storyLayout(media, groups) {
  const nodes = {};
  const edges = [];
  nodes.story = { kind: "story", pos: STORY_C };

  const R1 = media.length > 6 ? 205 : 185;
  spread(media.length, -172, -8).forEach((a, i) => {
    const m = media[i];
    nodes[`m:${m.id}`] = { kind: "media", media: m, angle: a, pos: polar(STORY_C, R1, a) };
    edges.push({ key: `e:m:${m.id}`, from: "story", to: `m:${m.id}`, color: m.color, bow: 0.12 });
  });

  const slotsOf = (g) => Math.min(g.items.length, MAX_LEAVES);
  const totalSlots = groups.reduce((n, g) => n + slotsOf(g), 0);
  const gap = groups.length > 1 ? 6 : 0;
  const usable = 164 - gap * (groups.length - 1);
  const step = Math.min(13, usable / Math.max(totalSlots, 1));
  let a = 90 - (step * totalSlots + gap * (groups.length - 1)) / 2 + step / 2;

  groups.forEach((g) => {
    const n = slotsOf(g);
    const hasMore = g.items.length > n;
    const shown = hasMore ? g.items.slice(0, n - 1) : g.items;
    const angles = Array.from({ length: n }, (_, i) => a + i * step);
    a += n * step + gap;
    const mid = (angles[0] + angles[n - 1]) / 2;
    const hub = `h:${g.id}`;
    nodes[hub] = { kind: "hub", group: g, pos: polar(STORY_C, 195, mid) };
    edges.push({ key: `e:${hub}`, from: "story", to: hub, color: g.color, bow: -0.1 });
    shown.forEach((it, j) => {
      const k = `l:${g.id}:${it.key}`;
      nodes[k] = { kind: "leaf", group: g, item: it, angle: angles[j], pos: polar(STORY_C, R3, angles[j]) };
      edges.push({ key: `e:${k}`, from: hub, to: k, color: g.color, bow: 0.08 });
    });
    if (hasMore) {
      const k = `more:${g.id}`;
      const ang = angles[n - 1];
      nodes[k] = { kind: "more", group: g, count: g.items.length - shown.length, angle: ang, pos: polar(STORY_C, R3, ang) };
      edges.push({ key: `e:${k}`, from: hub, to: k, color: g.color, bow: 0.08 });
    }
  });
  return { nodes, edges };
}

// ── Focus view: the hub in the centre, all of its stories in a full circle,
// the story itself small in the top-left corner.
function focusLayout(group) {
  const nodes = {};
  const edges = [];
  const hub = `h:${group.id}`;
  nodes[hub] = { kind: "hub", group, pos: FOCUS_C, focused: true };
  nodes.story = { kind: "story", pos: [110, 92], small: true };
  edges.push({ key: "e:back", from: hub, to: "story", color: group.color, bow: 0.15, faint: true });

  const n = group.items.length;
  const R = n <= 10 ? 230 : n <= 24 ? 265 : 290;
  group.items.forEach((it, i) => {
    const ang = norm(-90 + (360 * i) / n);
    const k = `l:${group.id}:${it.key}`;
    nodes[k] = { kind: "leaf", group, item: it, angle: ang, pos: polar(FOCUS_C, R, ang) };
    edges.push({ key: `e:${k}`, from: hub, to: k, color: group.color, bow: 0.06 });
  });
  return { nodes, edges };
}

export default function StoryGraph({ center, media, groups, renderMedia }) {
  const visibleGroups = useMemo(() => groups.filter((g) => g.items.length > 0), [groups]);
  const [focusId, setFocusId] = useState(null);
  const focusGroup = visibleGroups.find((g) => g.id === focusId) || null;
  const [hover, setHover] = useState(null);
  const [open, setOpen] = useState(null); // media node shown in the overlay
  const scrollRef = useRef(null);

  const story = useMemo(() => storyLayout(media, visibleGroups), [media, visibleGroups]);
  const focus = useMemo(() => (focusGroup ? focusLayout(focusGroup) : null), [focusGroup]);
  const current = focus || story;

  // Every node of both layouts stays mounted, so nodes glide between views
  // and the others fade in / out in place.
  const allKeys = useMemo(() => {
    const keys = new Set(Object.keys(story.nodes));
    if (focus) Object.keys(focus.nodes).forEach((k) => keys.add(k));
    return [...keys];
  }, [story, focus]);
  // Remember where focus-only nodes were, so they fade out there.
  const lastPos = useRef({});
  allKeys.forEach((k) => {
    const n = current.nodes[k];
    if (n) lastPos.current[k] = n;
  });

  // On narrow screens the graph scrolls sideways; start centred.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollWidth > el.clientWidth) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (open) setOpen(null);
      else setFocusId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const posOf = (k) => (current.nodes[k] || lastPos.current[k] || story.nodes[k]).pos;
  const isLit = (e) => hover && (e.from === hover || e.to === hover);

  const move = (k, visible) => ({
    transform: `translate(${posOf(k)[0]}px, ${posOf(k)[1]}px)`,
    transition: "transform .85s cubic-bezier(.22,.8,.24,1), opacity .5s ease",
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? "auto" : "none",
  });
  const hoverProps = (k) => ({ onMouseEnter: () => setHover(k), onMouseLeave: () => setHover(null) });

  // Draw order: leaves, more, hubs, media, story (later = on top).
  const rank = { leaf: 0, more: 1, hub: 2, media: 3, story: 4 };
  const ordered = [...allKeys].sort(
    (a, b) => rank[(story.nodes[a] || focus?.nodes[a]).kind] - rank[(story.nodes[b] || focus?.nodes[b]).kind]
  );

  const renderNode = (k) => {
    const live = current.nodes[k];
    const n = live || lastPos.current[k] || story.nodes[k];
    const visible = !!live;
    const g = n.group;

    if (n.kind === "leaf") {
      const a = norm(n.angle);
      const flip = Math.abs(a) > 90;
      const [tx, ty] = polar([0, 0], 22, a);
      return (
        <g key={k} style={move(k, visible)}>
          <g className="km-in">
            <Link to={n.item.to} {...hoverProps(k)}>
              <g className="km-hit">
                <title>{`${n.item.title}${n.item.sub ? ` — ${n.item.sub}` : ""}`}</title>
                <circle r="12" fill="#1A0A0E" stroke={g.color} strokeWidth="2" />
                <circle r="4.5" fill={g.color} />
                <text
                  transform={`translate(${tx},${ty}) rotate(${flip ? a - 180 : a})`}
                  textAnchor={flip ? "end" : "start"}
                  dy="5"
                  className="km-label font-krushna"
                  fontSize="15"
                  fill={hover === k ? g.color : "#F5E9C8"}
                >
                  {clip(n.item.title, 16)}
                </text>
              </g>
            </Link>
          </g>
        </g>
      );
    }

    if (n.kind === "more") {
      return (
        <g key={k} style={move(k, visible)}>
          <g className="km-in">
            <g className="km-hit" role="button" aria-label={`All ${g.items.length}: ${g.sub || g.label}`} onClick={() => setFocusId(g.id)} {...hoverProps(k)}>
              <circle r="17" fill="#1A0A0E" stroke={g.color} strokeWidth="2" strokeDasharray="3 3" />
              <text y="5" textAnchor="middle" fontSize="13" fontWeight="700" fill={g.color}>+{n.count}</text>
            </g>
          </g>
        </g>
      );
    }

    if (n.kind === "hub") {
      const focused = !!(live && live.focused);
      return (
        <g key={k} style={move(k, visible)}>
          <g className="km-in">
            <g className={focused ? "" : "km-float"} style={{ animationDelay: `${(n.pos[0] % 7) * 0.4}s` }}>
              <g
                className="km-hit"
                role="button"
                aria-label={focused ? `${g.focusTitle || g.label}` : `${g.label} ${g.sub || ""}`}
                onClick={() => (focused ? null : setFocusId(g.id))}
                {...hoverProps(k)}
              >
                <g style={{ transform: `scale(${focused ? 2.35 : 1})`, transition: "transform .85s cubic-bezier(.22,.8,.24,1)" }}>
                  <circle r="31" fill="#1A0A0E" stroke={g.color} strokeWidth={focused ? 1.4 : 2.5} />
                  <circle r="31" fill={g.color} opacity=".14" />
                  {g.focusImage && (
                    <image
                      href={g.focusImage}
                      x="-28" y="-28" width="56" height="56"
                      preserveAspectRatio="xMidYMid slice"
                      clipPath="url(#km-clip-hub)"
                      style={{ opacity: focused ? 0.5 : 0, transition: "opacity .6s" }}
                    />
                  )}
                  {!focused && (
                    <>
                      <text y="-2" textAnchor="middle" className="font-krushna" fontSize="17" fill={g.color}>{g.label}</text>
                      <text y="15" textAnchor="middle" fontSize="12" fill="#F5E9C8" opacity=".75">{g.items.length}</text>
                    </>
                  )}
                </g>
                {focused && (
                  <>
                    <circle r="76" fill="none" stroke={g.color} strokeWidth="2" className="km-pulse" />
                    <text
                      y="-4"
                      textAnchor="middle"
                      className="km-label font-krushna km-fadein"
                      fontSize={(g.focusTitle || g.label).length > 14 ? 16 : 21}
                      fill="#F5E9C8"
                    >
                      {clip(g.focusTitle || g.label, 24)}
                    </text>
                    <text y="18" textAnchor="middle" className="km-label km-fadein" fontSize="12" fill={g.color}>
                      {clip(g.focusSub || `${g.items.length}`, 28)}
                    </text>
                  </>
                )}
              </g>
            </g>
          </g>
        </g>
      );
    }

    if (n.kind === "media") {
      const m = n.media;
      const o = (() => {
        const [dx, dy] = polar([0, 0], 50, n.angle);
        const c = Math.cos(deg(n.angle));
        return { x: dx, y: dy, anchor: c < -0.35 ? "end" : c > 0.35 ? "start" : "middle" };
      })();
      const up = o.y < -20;
      return (
        <g key={k} style={move(k, visible)}>
          <g className="km-in">
            <g className="km-float" style={{ animationDelay: `${(n.pos[0] % 5) * 0.5}s` }}>
              <g className="km-hit" role="button" aria-label={`${m.label} ${m.sub || ""}`} onClick={() => setOpen(m)} {...hoverProps(k)}>
                <circle r="38" fill="#1A0A0E" stroke={m.color} strokeWidth="2.5" />
                {m.image ? (
                  <image href={m.image} x="-34" y="-34" width="68" height="68" preserveAspectRatio="xMidYMid slice" clipPath="url(#km-clip-media)" opacity=".9" />
                ) : (
                  <circle r="34" fill={m.color} opacity=".13" />
                )}
                {m.Icon && (
                  <g transform={m.image ? "translate(14,14)" : "translate(-13,-13)"}>
                    {m.image && <circle cx="9" cy="9" r="12" fill="#1A0A0E" stroke={m.color} strokeWidth="1.5" />}
                    <m.Icon x={m.image ? 2 : 0} y={m.image ? 2 : 0} size={m.image ? 14 : 26} color={m.color} />
                  </g>
                )}
                <text x={o.x} y={o.y + (up ? -12 : 4)} textAnchor={o.anchor} className="km-label font-krushna" fontSize="16" fill="#F5E9C8">{m.label}</text>
                {m.sub && (
                  <text x={o.x} y={o.y + (up ? 4 : 20)} textAnchor={o.anchor} className="km-label" fontSize="11" fill={m.color}>{clip(m.sub, 22)}</text>
                )}
              </g>
            </g>
          </g>
        </g>
      );
    }

    // the story
    const small = !!(live && live.small);
    return (
      <g key={k} style={move(k, true)}>
        <g style={{ transform: `scale(${small ? 0.55 : 1})`, transition: "transform .85s cubic-bezier(.22,.8,.24,1)" }}>
          {!small && <circle r="74" fill="none" stroke="#D4A843" strokeWidth="2" className="km-pulse" />}
          <g className="km-in">
            <g
              className="km-hit"
              role="button"
              aria-label={small ? `Back to ${center.title}` : center.title}
              onClick={small ? () => setFocusId(null) : center.onClick}
              {...hoverProps("story")}
            >
              <circle r="74" fill="#3D0F19" stroke="#D4A843" strokeWidth="3" />
              {center.photo && (
                <image href={center.photo} x="-66" y="-66" width="132" height="132" preserveAspectRatio="xMidYMid slice" clipPath="url(#km-clip-center)" opacity=".55" />
              )}
              <circle r="66" fill="#1A0A0E" opacity=".35" />
              <text y="2" textAnchor="middle" className="km-label font-krushna" fontSize="24" fill="#F5E9C8">{clip(center.title, 12)}</text>
              <text y="24" textAnchor="middle" className="km-label font-krushna" fontSize="12" fill="#E8C572">
                {small ? "← వెనక్కి · Back" : clip(center.subtitle, 24)}
              </text>
            </g>
          </g>
        </g>
      </g>
    );
  };

  return (
    <div className="relative rounded-2xl border border-kk-gold/15 bg-[#140609] overflow-hidden">
      {/* Breadcrumb while a hub is in focus */}
      {focusGroup && (
        <div className="absolute top-3 left-3 right-3 z-10 flex flex-wrap items-center gap-1.5 text-xs pointer-events-none">
          <button
            type="button"
            onClick={() => setFocusId(null)}
            className="pointer-events-auto flex items-center gap-1.5 px-3 py-1 rounded-full bg-kk-ink/90 border border-kk-gold/30 text-kk-gold hover:bg-kk-gold/15"
          >
            <FaArrowLeft className="text-[10px]" />
            <span className="font-krushna text-sm">{center.title}</span>
          </button>
          <span className="text-kk-cream/40">›</span>
          <span className="px-3 py-1 rounded-full bg-kk-ink/90 border" style={{ borderColor: focusGroup.color, color: focusGroup.color }}>
            <span className="font-krushna text-sm">{focusGroup.label}</span> ({focusGroup.items.length})
          </span>
        </div>
      )}

      <div ref={scrollRef} className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="block w-full min-w-[760px]"
          role="img"
          aria-label={`కథాజాలం · Story Graph: ${center.title}`}
        >
          <style>{`
            .km-edge { fill: none; stroke-width: 1.6; stroke-dasharray: 5 9; animation: km-flow 2.4s linear infinite; opacity: .45; transition: opacity .2s, stroke-width .2s; }
            .km-edge.km-faint { opacity: .25; }
            .km-edge.km-lit { opacity: 1; stroke-width: 2.6; }
            .km-edges { animation: km-fade .7s .45s both; }
            .km-fadein { animation: km-fade .6s .5s both; }
            .km-in { animation: km-in .6s cubic-bezier(.2,.8,.2,1.2) both; transform-box: fill-box; transform-origin: center; }
            .km-float { animation: km-float 5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
            .km-hit { cursor: pointer; transition: transform .2s; transform-box: fill-box; transform-origin: center; }
            .km-hit:hover { transform: scale(1.12); }
            .km-label { paint-order: stroke; stroke: #140609; stroke-width: 5px; stroke-linejoin: round; }
            .km-pulse { animation: km-pulse 3s ease-out infinite; transform-box: fill-box; transform-origin: center; }
            @keyframes km-flow { to { stroke-dashoffset: -56; } }
            @keyframes km-fade { from { opacity: 0; } to { opacity: 1; } }
            @keyframes km-in { from { opacity: 0; transform: scale(.4); } to { opacity: 1; transform: scale(1); } }
            @keyframes km-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
            @keyframes km-pulse { 0% { transform: scale(1); opacity: .55; } 100% { transform: scale(1.45); opacity: 0; } }
            @media (prefers-reduced-motion: reduce) {
              .km-edge, .km-float, .km-pulse, .km-in, .km-edges, .km-fadein { animation: none; }
              g { transition: none !important; }
            }
          `}</style>
          <defs>
            <radialGradient id="km-glow" cx="50%" cy="42%" r="60%">
              <stop offset="0%" stopColor="#6B1D2A" stopOpacity=".55" />
              <stop offset="60%" stopColor="#3D0F19" stopOpacity=".25" />
              <stop offset="100%" stopColor="#140609" stopOpacity="0" />
            </radialGradient>
            <clipPath id="km-clip-center"><circle cx="0" cy="0" r="66" /></clipPath>
            <clipPath id="km-clip-media"><circle cx="0" cy="0" r="34" /></clipPath>
            <clipPath id="km-clip-hub"><circle cx="0" cy="0" r="28" /></clipPath>
          </defs>

          <rect width={W} height={H} fill="url(#km-glow)" />
          {Array.from({ length: 44 }, (_, i) => (
            <circle key={i} cx={(i * 211) % W} cy={(i * 137) % H} r={(i % 3) * 0.5 + 0.5} fill="#E8C572" opacity=".15" />
          ))}

          {/* edges of the current view, re-drawn (faded in) after each move */}
          <g key={`edges:${focusId || "story"}`} className="km-edges">
            {current.edges.map((e) => (
              <path
                key={e.key}
                d={edgePath(posOf(e.from), posOf(e.to), e.bow)}
                stroke={e.color}
                className={`km-edge ${e.faint ? "km-faint" : ""} ${isLit(e) ? "km-lit" : ""}`}
              />
            ))}
          </g>

          {ordered.map(renderNode)}
        </svg>
      </div>

      <p className="absolute bottom-2 left-3 text-[11px] text-kk-cream/40 pointer-events-none">
        {focusGroup
          ? "Tap a story to travel there · tap the corner star or press Esc to go back"
          : "Tap a group to open it · tap a node to play it · tap a story to travel there"}
      </p>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setOpen(null)}>
          <div
            className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-kk-gold/25 bg-kk-ink p-4 md:p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-kk-cream"
              aria-label="Close"
            >
              <FaTimes />
            </button>
            <p className="font-krushna text-kk-gold text-xl pr-10">{open.label}</p>
            {open.sub && <p className="text-kk-cream/50 text-xs mb-3">{open.sub}</p>}
            {renderMedia(open)}
          </div>
        </div>
      )}
    </div>
  );
}
